import React, { useEffect, useMemo, useRef } from 'react';
import { styled, SxProp } from 'dripsy';
import { observer } from 'mobx-react-lite';
import { View, Platform } from 'react-native';
import { AVPlaybackSource, Video } from 'expo-av';
import { when } from 'mobx';
import { useIsFocused } from '@react-navigation/native';

import { Course } from '@models/Courses/CourseModel';
import {
  CoverView,
  FullScreenVideoPlayer,
  Icon,
  Image,
  Touchable,
  TouchableTypeEnum,
} from '@components';
import { createAspectRatioStyles } from '@styles';
import { useAuthModel } from '@models/AuthModel';

const Container = styled(View)({
  overflow: 'hidden',
  borderRadius: 12,
  bg: '$background20',
  ...createAspectRatioStyles(16 / 9, '100%'),
});

const VideoThumbnailTouchable = styled(Touchable)({
  flex: 1,
});

const VideoThumbnailImage = styled(Image)({
  flex: 1,
});

const CoverViewContainer = styled(CoverView)({
  flex: 1,
});

const coverViewStyles: SxProp = {
  alignItems: 'center',
  justifyContent: 'center',
};

export interface ICourseIntroVideoPlayerProps {
  course: Course;
}

export const CourseIntroVideoPlayer =
  observer<ICourseIntroVideoPlayerProps>(({ course }) => {
    const auth = useAuthModel();
    const isFocused = useIsFocused();
    const videoPlayerRef = useRef<Video>(null);
    const fullScreenVideoPlayerRef = useRef<Video>(null);

    const [isShowPlayer, setIsShowPlayer] = React.useState(false);

    useEffect(() => {
      if (!isFocused) {
        videoPlayerRef.current?.pauseAsync();
        fullScreenVideoPlayerRef.current?.pauseAsync();
      }
    }, [isFocused]);

    function showVideoPlayer() {
      setIsShowPlayer(true);
    }

    const source = useMemo<AVPlaybackSource>(
      () => ({
        uri: course.introVideo.streamSource,
        overrideFileExtensionAndroid: 'm3u8',
      }),
      [course.id],
    );

    async function refreshToken() {
      if (auth.renewTokens.inProgress) {
        return when(() => auth.renewTokens.inProgress);
      }

      try {
        await auth.renewTokens.run();
      } catch (error) {
        // Do nothing
      }
    }

    return (
      <Container>
        {isShowPlayer ? (
          <FullScreenVideoPlayer
            source={source}
            shouldPlay
            hasSubtitles={false}
            onRefreshTokens={refreshToken}
            videoPlayerRef={videoPlayerRef}
            fullScreenVideoPlayerRef={fullScreenVideoPlayerRef}
          />
        ) : (
          <VideoThumbnailTouchable
            onPress={showVideoPlayer}
            type={TouchableTypeEnum.WithoutFeedback}
          >
            <CoverViewContainer
              isVisible
              coverComponentSx={coverViewStyles}
              CoverComponent={() => (
                <Icon
                  name="play1-bold"
                  size={Platform.select({ web: 60, default: 40 })}
                  color="$background0"
                />
              )}
            >
              <VideoThumbnailImage
                source={{
                  uri: course.poster.normal?.url,
                }}
              />
            </CoverViewContainer>
          </VideoThumbnailTouchable>
        )}
      </Container>
    );
  });
